// frontend\src\components\analytics\AnalyticsLineChart.tsx

import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ScriptableContext } from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  AnalyticsDataPoint,
  AnalyticsCompareMode,
  AnalyticsCompareSeries,
  AnalyticsRevenueBasis,
} from '../../types';
import ErrorBoundary from '../ErrorBoundary';
import RevenueBasisInfo from './RevenueBasisInfo';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// One colour per comparison period, nearest period first (darkest -> lightest).
const COMPARE_COLORS = ['#8A8F98', '#B0A17A', '#7FA6A0', '#B08A9E', '#9A9AC2'];

// Vertical gradient under the primary line, matching --color-accent (#3B7DD8)
const accentGradient = (context: ScriptableContext<'line'>) => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return 'rgba(59, 125, 216, 0.08)';
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, 'rgba(59, 125, 216, 0.18)');
  gradient.addColorStop(1, 'rgba(59, 125, 216, 0)');
  return gradient;
};

interface AnalyticsLineChartProps {
  data: AnalyticsDataPoint[];
  compareData?: AnalyticsDataPoint[];
  compareSeries?: AnalyticsCompareSeries[];
  metric: 'sales' | 'orderCount' | 'itemCount';
  totalMetric: number;
  percentageChange: number | null;
  startDate: string;
  endDate: string;
  compareMode: AnalyticsCompareMode;
  compareFilterLabel?: string;
  revenueBasis?: AnalyticsRevenueBasis;
  /** Plain-English description of the active filters, shown above the total. */
  filterSummary?: string;
  formatFunction?: (value: number, metric: string) => string;
}

const AnalyticsLineChart: React.FC<AnalyticsLineChartProps> = ({
  data,
  compareData,
  compareSeries,
  metric,
  totalMetric,
  percentageChange,
  startDate,
  endDate,
  compareMode,
  compareFilterLabel,
  revenueBasis,
  filterSummary,
  formatFunction,
}) => {
  const formatMetric = (value: number, metric: string) => {
    if (formatFunction) {
      return formatFunction(value, metric);
    }
    if (metric === 'sales') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
    return value.toString();
  };

  const formatDateRange = (start: string, end: string) => {
    if (!start || !end) return '';
    const parseDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    const startFormatted = parseDate(start).toLocaleDateString('en-US', options);
    const endFormatted = parseDate(end).toLocaleDateString('en-US', options);
    return `${startFormatted} - ${endFormatted}`;
  };

  // Comparison legends can stack up to five deep, so keep them terser than the primary label.
  const formatCompactRange = (start: string, end: string) => {
    if (!start || !end) return '';
    const parseDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const startDateObj = parseDate(start);
    const endDateObj = parseDate(end);
    const sameYear = startDateObj.getFullYear() === endDateObj.getFullYear();
    const startFormatted = startDateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
    const endFormatted = endDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startFormatted} – ${endFormatted}`;
  };

  // Prefer the ranges the API resolved; fall back to a single unlabelled series for older payloads.
  const resolvedCompareSeries: AnalyticsCompareSeries[] =
    compareSeries && compareSeries.length > 0
      ? compareSeries
      : compareData && compareData.length > 0
      ? [{ startDate: '', endDate: '', offset: 1, data: compareData, label: compareFilterLabel }]
      : [];

  const seriesLabel = (series: AnalyticsCompareSeries) =>
    series.label || formatCompactRange(series.startDate, series.endDate) || 'Comparison';

  const lineChartData = {
    labels: data.map((d) => d.time || ''),
    datasets: [
      {
        label: formatDateRange(startDate, endDate),
        data: data.map((d) => Number(d[metric]) || 0),
        borderColor: '#3B7DD8',
        borderWidth: 2.5,
        backgroundColor: accentGradient,
        fill: true,
        pointStyle: false as const,
        tension: 0.35,
      },
      ...resolvedCompareSeries.map((series, index) => ({
        label: seriesLabel(series),
        data: series.data.map((d) => Number(d[metric]) || 0),
        borderColor: COMPARE_COLORS[index % COMPARE_COLORS.length],
        borderWidth: 2,
        borderDash: index === 0 ? [6, 4] : [3, 3],
        backgroundColor: 'rgba(0, 0, 0, 0)',
        fill: false,
        pointStyle: false as const,
        tension: 0.35,
      })),
    ],
  };

  return (
    <div className="analytics-card">
      <div className="analytics-total-value">
        {filterSummary && <div className="analytics-filter-summary">{filterSummary}</div>}
        <div className="analytics-total-value-row">
          {formatMetric(totalMetric || 0, metric)}
          {metric === 'sales' && <RevenueBasisInfo basis={revenueBasis} />}
          {percentageChange !== null && (
            <span className={`analytics-percentage-change ${percentageChange < 0 ? 'negative' : ''}`}>
              {percentageChange >= 0 ? '↑' : '↓'} {Math.abs(percentageChange).toFixed(1)}%
            </span>
          )}
        </div>
        {resolvedCompareSeries.map((series, index) => (
          <div className="analytics-total-value-comparison" key={`${series.startDate}-${series.offset}-${index}`}>
            <span
              className="analytics-compare-swatch"
              style={{ backgroundColor: COMPARE_COLORS[index % COMPARE_COLORS.length] }}
            />
            {formatMetric(
              series.data.reduce((sum, row) => sum + (Number(row[metric]) || 0), 0),
              metric
            )}{' '}
            ({seriesLabel(series)})
          </div>
        ))}
      </div>
      <ErrorBoundary>
        {lineChartData.labels.length > 0 ? (
          <div style={{ minHeight: '400px' }}>
            <Line
              data={lineChartData}
              options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    font: {
                      family: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
                      size: 13,
                    },
                    color: '#5A5A56',
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'line',
                  },
                },
                tooltip: {
                  backgroundColor: '#ffffff',
                  titleColor: '#1C1C1A',
                  bodyColor: '#1C1C1A',
                  borderColor: '#E8E8E4',
                  borderWidth: 1,
                  cornerRadius: 10,
                  padding: 12,
                  boxPadding: 4,
                  callbacks: {
                    label: (context) => {
                      const value = context.raw as number;
                      // Dataset 0 is the selection; the rest map onto the comparison series in order.
                      const series = resolvedCompareSeries[context.datasetIndex - 1];
                      const cell = series ? series.data[context.dataIndex]?.time ?? context.label : context.label;
                      return `${cell}: ${formatMetric(value, metric)}`;
                    },
                  },
                },
              },
              interaction: {
                mode: 'index',
                intersect: false,
              },
              scales: {
                y: {
                  beginAtZero: true,
                  border: { display: false },
                  grid: { color: 'rgba(0, 0, 0, 0.05)' },
                  ticks: { color: '#8F8F8A', font: { family: "'DM Mono', monospace", size: 11 } },
                  title: {
                    display: true,
                    text: metric === 'sales' ? 'Revenue' : metric === 'orderCount' ? 'Orders' : 'Items Sold',
                    color: '#5A5A56',
                    font: { family: "'DM Sans', sans-serif", size: 12 },
                  },
                },
                x: {
                  border: { display: false },
                  grid: { display: false },
                  ticks: { color: '#8F8F8A', font: { family: "'DM Sans', sans-serif", size: 11 }, maxRotation: 45 },
                },
              },
            }}
            />
          </div>
        ) : (
          <p>No data available.</p>
        )}
      </ErrorBoundary>
    </div>
  );
};

export default AnalyticsLineChart;